/**
 * Adds an entry to the session storage under the specified key.
 * If the entry already exists in the stored array, it will not be added again.
 *
 * @param key - The key under which the entry will be stored in session storage.
 * @param entry - The entry to be added to the session storage.
 * @throws Will handle any errors that occur during JSON parsing or stringifying.
 */
export const addEntryToSessionStorage = (key: string, entry: any): void => {
  try {
    const entries: any[] = JSON.parse(window.sessionStorage.getItem(key) || '[]')

    if (!entries.includes(entry)) {
      entries.push(entry)
      window.sessionStorage.setItem(key, JSON.stringify(entries))
    }
  } catch (e) {
    window.sessionStorage.setItem(key, JSON.stringify([entry]))
  }
}

/**
 * Removes an entry from the session storage under the specified key.
 * If the entry does not exist in the stored array, no changes will be made.
 *
 * @param key - The key under which the entry is stored in session storage.
 * @param entry - The entry to be removed from the session storage.
 * @throws Will handle any errors that occur during JSON parsing or stringifying.
 */
export const removeEntryFromSessionStorage = (key: string, entry: any): void => {
  try {
    const entries: any[] = JSON.parse(window.sessionStorage.getItem(key) || '[]')

    const index = entries.indexOf(entry)
    if (index !== -1) {
      entries.splice(index, 1)
    }

    window.sessionStorage.setItem(key, JSON.stringify(entries))
  } catch (e) {
    window.sessionStorage.setItem(key, JSON.stringify([]))
  }
}

/**
 * Retrieves all entries stored under the specified key in session storage.
 * If the key does not exist or the data cannot be parsed, an empty array is returned.
 *
 * @typeParam T - The type of the entries stored in session storage. Defaults to `any`.
 * @param key - The key under which the entries are stored in session storage.
 * @returns An array of entries of type `T` stored under the specified key.
 * @throws Will handle any errors that occur during JSON parsing.
 */
export const getEntriesFromSessionStorage = <T = any>(key: string): T[] => {
  try {
    return JSON.parse(window.sessionStorage.getItem(key) || '[]')
  } catch (e) {
    return []
  }
}

